import { useContext, useEffect, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import { gql, useQuery } from '@apollo/client';
import _, { capitalize } from 'lodash';
import { useLatest } from 'react-use';
import { customLayerSpec, userLayers } from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import {
  InRowPatternsQuery,
  MapDataQuery,
  SectionGroupsQuery,
} from '../../queries';
import { parseRemoteSections } from '../../utils/field';
import { useQueryWithFarmId } from '../../utils/hooks';
import { forceMapResize } from '../../utils/map';
import { SECTION_CROP_META_VERSION } from '../../utils/meta';
import {
  cleanUpFeaturesInDev,
  fillLayerIds,
  getLayerId,
  layerIsCustom,
} from '../../utils/layers';
import { sectionHasGeodata } from '../../utils/sections';
import useSyncActiveSectionGroup from '../../hooks/useSyncActiveSectionGroup';
import useReadonly from '../../hooks/useReadonly';
import { showError, showWarning } from '../../utils/toast';
import { notifyMonitoring } from '../../utils/monitoring';
import { getFirstQueryResult } from '../../utils/graphql';

const LayerSignedUrlQuery = gql`
  query LayerSignedUrl($id: ID!) {
    layer(id: $id) {
      id
      signedUrl
    }
  }
`;

const SectionSignedUrlQuery = gql`
  query SectionSignedUrl($id: ID!, $includeVersionId: Boolean) {
    section(id: $id) {
      id
      signedUrl(includeVersionId: $includeVersionId)
    }
  }
`;

const fetchingDataReducer = (state, action) => {
  const newState = { ...state };
  newState[action.index] = action.value;
  return newState;
};

const readLayerS3Data = (layer, getSignedUrl = null) => {
  const { signedUrl } = layer;
  const layerId = getLayerId(layer);
  const layerName = layer.displayName || capitalize(layerId);

  return fetch(signedUrl, {
    headers: {
      accept: 'application/json',
    },
  }).then((response) => {
    if (response.ok) return response.json();
    // Retry with a new pre-signed URL in case timeout has been reached
    if ([403, 404].includes(response.status) && getSignedUrl != null) {
      // Get pre-signed GET URL
      return getSignedUrl({ id: layer.id }).then(({ data }) => {
        const retryLayer = {
          ...layer,
          signedUrl: getFirstQueryResult(data).signedUrl,
        };
        return readLayerS3Data(retryLayer);
      });
    }
    // Key has no data, allow the layer to be loaded to recover from this state
    if ([403, 404].includes(response.status)) {
      return { warning: `No data for layer ${layerName}` };
    }
    throw new Error(response.statusText);
  });
};

const parseRemoteLayer = (remoteLayer, getSignedUrl = null) => {
  const layer = { ...remoteLayer };
  const isCustom = layerIsCustom(layer);
  const layerId = getLayerId(layer);
  const layerName = layer.displayName || capitalize(layerId);

  return readLayerS3Data(layer, getSignedUrl)
    .then((data) => {
      // Convert layerFeatures array to layerFeatureRecords object keyed by id:
      layer.layerFeatureRecords = _.keyBy(layer.layerFeatures, 'id');
      delete layer.layerFeatures;
      const { error, warning } = data;

      // Ensure all features have ids
      // NOTE: Ids will be ephemeral for this session if this user doesn't have
      // write access to the farm.
      let { dataWithIds, addedIds: dataModified } = fillLayerIds(data, layerId);
      const featuresShown = dataWithIds.features.reduce(
        (acc, feature) => ({ ...acc, [feature.properties.id]: true }),
        {},
      );

      if (isCustom) {
        // clean up dev data
        const { cleanedUpData, modified } = cleanUpFeaturesInDev(
          dataWithIds,
          layer.layerFeatureRecords,
        );
        dataWithIds = cleanedUpData;
        if (modified) dataModified = true;

        return {
          error,
          warning,
          ...layer,
          ...customLayerSpec,
          featuresShown,
          shown: true,
          data: dataWithIds,
          loadSkipped: false,
          dataModified,
        };
      }

      // pull in width from remote data
      const { width } = layer;
      // use default layer width if width not specified in remote data
      const { width: defaultWidth } = userLayers[layerId];
      return {
        error,
        warning,
        featuresShown,
        width: width || defaultWidth,
        data: dataWithIds,
        dataModified,
        // NOTE: we don't pull in updatedAt from the remote data because
        // that's only needed for create/delete layer mutations, which we
        // don't use with non-custom layers.
      };
    })
    .catch((e) => {
      const error = `Error fetching layer data for ${layerName}`;
      notifyMonitoring([error, e].join(': '));
      return { error };
    });
};

/**
 * All the non-presentation logic for `FieldsView` and `FieldsPreviewView`.
 */
const FieldsViewContainer = ({ children }) => {
  const { farmId } = useParams();
  const readonly = useReadonly();

  const { refetch: getSectionSignedUrl } = useQuery(SectionSignedUrlQuery, {
    skip: true,
  });
  const { refetch: getLayerSignedUrl } = useQuery(LayerSignedUrlQuery, {
    skip: true,
  });

  // Use no-cache fetch policy in this view to ensure we always get the latest data.
  // Queries that reference crops, sections, or layers should use this as well to avoid
  // referencing data that's no longer in the state.
  const { data: remoteData } = useQueryWithFarmId(MapDataQuery, {
    fetchPolicy: 'no-cache',
  });
  const { data: remoteSectionGroupsData } = useQueryWithFarmId(
    SectionGroupsQuery,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const { data: remoteInRowPatternsData } = useQueryWithFarmId(
    InRowPatternsQuery,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const {
    crops: { addCrops, availableCrops },
    fields: { addFieldSections, fieldSections },
    sectionGroups: { replaceSectionGroups },
    patterns: { replaceInRowPatterns },
    layers: { setLayerData, setLayer, layers: contextLayers },
    resetState,
  } = useContext(MapContext);

  const contextLayersRef = useLatest(contextLayers);

  const [fetchingData, fetchingDataDispatch] = useReducer(
    fetchingDataReducer,
    {},
  );
  const setFetchingData = (layerId, value) => {
    fetchingDataDispatch({ index: layerId, value });
  };

  // Don't sync the section groups until both section groups and sections are available.
  useSyncActiveSectionGroup(
    fieldSections?.length > 0 && remoteSectionGroupsData?.farm?.sectionGroups,
  );

  // Ensure that the remote crops/patterns are available before adding sections
  const [cropsAdded, setCropsAdded] = useState(false);
  const [patternsAdded, setPatternsAdded] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => resetState(), []);

  useEffect(() => {
    forceMapResize();
  }, []);

  const parseRemoteAndSetLayer = (remoteLayer) => {
    const layerId = getLayerId(remoteLayer);

    // If we're already in the process of fetching the data, return
    if (fetchingData[layerId]) return;

    const contextLayer = contextLayersRef.current?.[layerId];
    if (contextLayer && !contextLayer.shown && !contextLayer.data) {
      // only set loadSkipped if it hasn't been set yet
      // needed to signal to LayersTree that we still want to display this layer
      if (!contextLayer.loadSkipped) {
        setLayer(layerId, {
          loadSkipped: true,
          hasRemote: true,
        });
      }
      return;
    }

    if (contextLayer && contextLayer.data) {
      // Don't overwrite existing data
      return;
    }

    setFetchingData(layerId, true);
    parseRemoteLayer(remoteLayer, getLayerSignedUrl)
      .then((parsedLayer) => {
        const { dataModified, error, warning } = parsedLayer;
        if (error) {
          showError(error);
          return;
        }
        // Allow recovery from warning
        if (warning) showWarning(warning);
        setLayer(layerId, parsedLayer);

        // Upload data if it was modified
        const upload = dataModified && !readonly;
        if (upload) {
          setLayerData({ layerId, farmId, data: parsedLayer.data, upload });
        }
      })
      .finally(() => setFetchingData(layerId, undefined));
  };

  useEffect(() => {
    if (!remoteData) return;

    const {
      availableCrops: remoteAvailableCrops,
      farm: { layers },
    } = remoteData;

    // Add crops
    addCrops(remoteAvailableCrops);
    setCropsAdded(true);

    // Add layers
    layers.forEach(parseRemoteAndSetLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this hook should only be run once, when remoteData is ready
  }, [remoteData]);

  // Load any skipped layers that are shown when contextLayers changes
  useEffect(() => {
    if (!remoteData) return;

    const {
      farm: { layers },
    } = remoteData;

    Object.values(contextLayersRef.current)
      .filter(({ data, loadSkipped, shown }) => !data && loadSkipped && shown)
      .forEach((contextLayer) => {
        const remoteLayer = layers.find(
          (l) => getLayerId(l) === contextLayer.layerId,
        );
        if (!remoteLayer) return;

        parseRemoteAndSetLayer(remoteLayer);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextLayers]);

  const sectionGroups = remoteSectionGroupsData?.farm?.sectionGroups;
  useEffect(() => {
    if (!sectionGroups) return;
    replaceSectionGroups(sectionGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteSectionGroupsData?.farm?.sectionGroups]);

  const inRowPatterns = remoteInRowPatternsData?.farm?.inRowPatterns;
  useEffect(() => {
    if (!inRowPatterns) return;
    replaceInRowPatterns(inRowPatterns);
    setPatternsAdded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inRowPatterns]);

  useEffect(() => {
    // addFieldSections relies on availableCrops and inRowPatternsMap being
    // populated to compute the section crop metas, so this has to happen
    // on a subsequent render.
    if (
      !remoteData ||
      !cropsAdded ||
      !remoteInRowPatternsData ||
      !patternsAdded ||
      // We have already populated it (returns false if null or undefined)
      fieldSections?.length > 0
    ) {
      return;
    }
    const {
      farm: { sections: remoteSections },
    } = remoteData;

    // Add sections
    const errors = [];
    const warnings = [];
    Promise.all(parseRemoteSections(remoteSections, getSectionSignedUrl))
      .then((parsedSections) => {
        // Handle any failures
        parsedSections
          .filter((s) => s.error || s.warning)
          .forEach(({ name, error, warning }) => {
            if (error) {
              console.error(error);
              errors.push(name);
            }
            if (warning) warnings.push(warning);
          });
        // Filter only the valid sections
        return parsedSections.filter((s) => !s.error && sectionHasGeodata(s));
      })
      .then((newSections) => {
        // We would not normally upload here since we just got these from the server,
        // but this is a way to ensure the section crop metas stored in the database
        // are up to date since they are computed clientside.
        addFieldSections(newSections, {
          uploadMetadata:
            !readonly &&
            remoteSections.some(
              ({ sectionCropMetaVersion }) =>
                sectionCropMetaVersion < SECTION_CROP_META_VERSION,
            ),
          uploadGeodata: false,
        });
      })
      .finally(() => {
        // Set submission message for any failures
        if (errors.length) {
          showError(`Error fetching section data for ${errors.join(', ')}`);
        }
        if (warnings.length) {
          const allWarnings = (
            <div>
              {warnings.map((w) => (
                <div key={w}>{w}</div>
              ))}
            </div>
          );
          showWarning(allWarnings);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    availableCrops,
    cropsAdded,
    patternsAdded,
    remoteData,
    remoteInRowPatternsData,
  ]);

  const colors = remoteData?.colors || [];

  // If children is a render function, pass it additional data:
  return typeof children === 'function' ? children({ colors }) : children;
};

export default FieldsViewContainer;
